import { useState, useEffect } from 'react';
import { Bell, Settings, Volume2, VolumeX, Smartphone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { usePushNotifications } from '@/hooks/usePushNotifications';

interface NotificationSettings {
  pushEnabled: boolean;
  emailEnabled: boolean;
  soundEnabled: boolean;
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
  };
  categories: {
    guards: boolean;
    teams: boolean;
    schedules: boolean;
    reports: boolean;
    alerts: boolean;
    missions: boolean;
  };
}

export const NotificationCenter = () => {
  const [settings, setSettings] = useState<NotificationSettings>({
    pushEnabled: false,
    emailEnabled: true,
    soundEnabled: true,
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    },
    categories: {
      guards: true,
      teams: true,
      schedules: true,
      reports: false,
      alerts: true,
      missions: true
    }
  });

  const { 
    isSupported, 
    permission, 
    isSubscribed, 
    subscribe, 
    unsubscribe,
    sendNotification
  } = usePushNotifications();

  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    const saved = localStorage.getItem('shiftask-notification-settings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to parse notification settings:', error);
      }
    }
  };

  const saveSettings = (newSettings: NotificationSettings) => {
    setSettings(newSettings);
    localStorage.setItem('shiftask-notification-settings', JSON.stringify(newSettings));
    toast({
      title: "Settings Saved",
      description: "Your notification preferences have been updated."
    });
  };

  const handlePushToggle = async (enabled: boolean) => {
    if (enabled) {
      const success = await subscribe();
      if (success) {
        saveSettings({ ...settings, pushEnabled: true });
      }
    } else {
      const success = await unsubscribe();
      if (success) {
        saveSettings({ ...settings, pushEnabled: false });
      }
    }
  };

  const testNotification = () => {
    if (settings.pushEnabled && permission === 'granted') {
      sendNotification({
        title: 'Shiftask Test Notification',
        body: 'This is a test notification to verify your settings are working.',
        tag: 'test-notification'
      });
    } else {
      toast({
        title: "Test Failed",
        description: "Push notifications are not enabled or permission not granted.",
        variant: "destructive"
      });
    }
  };

  const updateCategory = (category: keyof typeof settings.categories, enabled: boolean) => {
    saveSettings({
      ...settings,
      categories: {
        ...settings.categories,
        [category]: enabled
      }
    });
  };

  const updateQuietHours = (field: string, value: string | boolean) => {
    saveSettings({
      ...settings,
      quietHours: {
        ...settings.quietHours,
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="w-6 h-6 text-primary" />
        <div>
          <h3 className="text-lg font-semibold">Notification Center</h3>
          <p className="text-sm text-muted-foreground">
            Manage how and when you receive notifications
          </p>
        </div>
      </div>

      {/* Push Notifications */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-muted-foreground" />
            <div>
              <h4 className="font-medium">Push Notifications</h4>
              <p className="text-sm text-muted-foreground">
                Get instant notifications on this device
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSupported ? (
              <>
                <Badge variant={permission === 'granted' ? "default" : permission === 'denied' ? "destructive" : "secondary"}>
                  {permission === 'granted' ? 'Enabled' : permission === 'denied' ? 'Blocked' : 'Not Set'}
                </Badge>
                <Switch
                  checked={settings.pushEnabled && permission === 'granted'}
                  onCheckedChange={handlePushToggle}
                  disabled={permission === 'denied'}
                />
              </>
            ) : (
              <Badge variant="destructive">Not Supported</Badge>
            )}
          </div>
        </div>

        {permission === 'denied' && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md mb-4">
            <h5 className="font-medium text-destructive mb-2">Notifications Blocked</h5>
            <p className="text-sm text-destructive/80 mb-3">
              To enable notifications: Click the lock icon (🔒) in your address bar, then allow notifications and refresh the page.
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.reload()}
              className="border-destructive/30 text-destructive hover:bg-destructive/10"
            >
              Refresh Page
            </Button>
          </div>
        )}

        {isSupported && permission === 'granted' && isSubscribed && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={testNotification}>
              Test Notification
            </Button>
          </div>
        )}

        {isSupported && permission === 'default' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 mb-3">
              Click the switch above to enable push notifications for important updates.
            </p>
          </div>
        )}
      </Card>

      {/* Email Notifications */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-muted-foreground" />
            <div>
              <h4 className="font-medium">Email Notifications</h4>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
          </div>
          <Switch
            checked={settings.emailEnabled}
            onCheckedChange={(enabled) => saveSettings({ ...settings, emailEnabled: enabled })}
          />
        </div>
      </Card>

      {/* Sound Settings */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.soundEnabled ? (
              <Volume2 className="w-5 h-5 text-muted-foreground" />
            ) : (
              <VolumeX className="w-5 h-5 text-muted-foreground" />
            )}
            <div>
              <h4 className="font-medium">Sound Notifications</h4>
              <p className="text-sm text-muted-foreground">
                Play sound for notifications
              </p>
            </div>
          </div>
          <Switch
            checked={settings.soundEnabled}
            onCheckedChange={(enabled) => saveSettings({ ...settings, soundEnabled: enabled })}
          />
        </div>
      </Card>

      {/* Quiet Hours */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Quiet Hours</h4>
              <p className="text-sm text-muted-foreground">
                Disable notifications during specific hours
              </p>
            </div>
            <Switch
              checked={settings.quietHours.enabled}
              onCheckedChange={(enabled) => updateQuietHours('enabled', enabled)}
            />
          </div>

          {settings.quietHours.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quiet-start">Start Time</Label>
                <Select value={settings.quietHours.start} onValueChange={(value) => updateQuietHours('start', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      return (
                        <SelectItem key={`${hour}:00`} value={`${hour}:00`}>
                          {hour}:00
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="quiet-end">End Time</Label>
                <Select value={settings.quietHours.end} onValueChange={(value) => updateQuietHours('end', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      return (
                        <SelectItem key={`${hour}:00`} value={`${hour}:00`}>
                          {hour}:00
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Notification Categories */}
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium">Notification Categories</h4>
            <p className="text-sm text-muted-foreground">
              Choose which types of notifications you want to receive
            </p>
          </div>

          <div className="space-y-3">
            {Object.entries(settings.categories).map(([category, enabled]) => (
              <div key={category} className="flex items-center justify-between">
                <Label className="capitalize font-normal">{category}</Label>
                <Switch
                  checked={enabled}
                  onCheckedChange={(checked) => updateCategory(category as keyof typeof settings.categories, checked)}
                />
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};